// @ts-strict-ignore
import { useCallback, useState, MutableRefObject, useEffect } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { createEditor, Descendant, Editor } from 'slate';
import { withHistory } from 'slate-history';
import { withReact } from 'slate-react';
import i18n from 'src/utils/translate';
import { useDispatch, useSelector } from 'src/store/store';
import { withMentions } from 'src/components/textEditor/components/mentionsPlugins';
import { withImages } from 'src/components/textEditor/components/imagesPlugin';
import { MyEditor, useEditor } from 'src/components/textEditor/customHelpers';
import { getEditorData } from 'src/components/textEditor/serialiseContent';
import EditorToolbar from 'src/components/textEditor/components/editorToolbar';
import Button from 'src/components/buttons/button';
import RichTextEditor from 'src/components/textEditor/richTextEditor';
import { EditorContainer } from 'src/modules/community/write/components/sharedStyles';
import { showGuidelines } from 'src/modules/community/shared/components/communityGuidelinesModal';
import { showModal, showSignupModal } from 'src/modules/shared/modal/actions';
import { setAlert } from 'src/components/alert/actions';
import { getLocalStorageItem, Key } from 'src/utils/localStorage';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { ModalType } from 'src/constants/modalTypes';
import { EMAIL_IN_TEXT, PHONE_NUMBER, URL_RE } from 'src/constants/regex';
import { MAX_CHARS_REPLY_BODY } from 'src/constants/write';
import { validateReply } from 'src/modules/community/singlePost/components/replies/validateReply';
type EditorProps = {
  autoFocus?: boolean;
  buttonLabel: string;
  replyEditorValue: Descendant[];
  postOrEdit: (editorData: {
    body: string;
    mentionedUserIds: number[];
    image?: {
      url?: string;
      hash?: string;
      file?: File;
      description?: string;
    };
  }) => void;
  respondRef?: MutableRefObject<Editor | null>;
};
export default function ReplyEditor({
  autoFocus = false,
  buttonLabel,
  postOrEdit,
  replyEditorValue,
  respondRef
}: EditorProps): JSX.Element {
  const dispatch = useDispatch();
  const editor = useEditor(withImages(withMentions(withReact(withHistory(createEditor())))), respondRef);
  const {
    currentUser,
    currentCommunity
  } = useSelector(state => state.context);
  const {
    composeReply
  } = useSelector(state => state.pages.singlePost);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isEmojiPickerClosed, setIsEmojiPickerClosed] = useState(false);
  const canSubmit = (): boolean => {
    if (!currentUser) {
      dispatch(showSignupModal());
      trackFootfallEvent(Event.Clicked, {
        clickType: 'sign-up',
        clickMetadata: {
          element: Element.Reply,
          section: Section.PostReply
        }
      });
      setIsSubmitDisabled(false);
      return false;
    }
    return true;
  };
  const submitReply = () => {
    setIsEmojiPickerClosed(true);
    if (canSubmit()) {
      const revalidateErrors = validateReply({
        editor,
        setIsDirty,
        setIsSubmitDisabled
      });
      if (revalidateErrors.length) {
        return dispatch(setAlert(revalidateErrors[revalidateErrors.length - 1]));
      }
      const {
        image,
        text,
        mentionedUserIds
      } = getEditorData(editor);
      const editorData = {
        body: text,
        mentionedUserIds,
        image
      };
      if (!getLocalStorageItem(Key.privacyReminderDismissed) && (EMAIL_IN_TEXT.test(text) || PHONE_NUMBER.test(text) && !URL_RE.test(text) || !!image?.file)) {
        // if image, only trigger modal when new image is being uploaded
        return dispatch(showModal({
          modalType: ModalType.PrivacyReminder,
          modalProps: {
            onCancel: () => setIsSubmitDisabled(false),
            onAccept: () => postOrEdit(editorData),
            data: {
              text,
              image
            }
          }
        }));
      }
      postOrEdit(editorData);
    }
  };
  useEffect(() => {
    if (!isSubmitDisabled && composeReply.submittedReplyId) {
      MyEditor.reset(editor);
      setIsSubmitDisabled(true);
      setIsEmojiPickerClosed(false); // resetting picker
      trackFootfallEvent(Event.Clicked, {
        clickType: 'completed-reply'
      });
    }
  }, [composeReply.submittedReplyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackAddImg = () => trackFootfallEvent(Event.Clicked, {
    clickType: 'image uploaded',
    clickMetadata: {
      Section: Section.PostReply
    }
  });
  const debouncedValidate = useCallback(debounce(() => validateReply({
    editor,
    setIsDirty,
    setIsSubmitDisabled
  }), 200), []);
  useUnsavedChangesPrompt({
    unsavedChanges: isDirty,
    onAccept: () => MyEditor.reset(editor)
  });
  return <ReplyEditorWrapper data-testid="reply-field" data-sentry-element="ReplyEditorWrapper" data-sentry-component="ReplyEditor" data-sentry-source-file="replyEditor.tsx">
      {replyEditorValue && <RichTextEditor autoFocus={autoFocus} editor={editor} initialValue={replyEditorValue} maxChars={MAX_CHARS_REPLY_BODY} placeholder={i18n.t('Add a comment here...')} toolbar={<StyledToolbar isEmojiPickerClosed={isEmojiPickerClosed} onAddImg={trackAddImg}>
              <ReplyButton ariaLabel="Submit reply" dataTestId="reply-box-reply-button" disabled={isSubmitDisabled || composeReply?.isLoading} // if any other reply is being sent, block submit btn
      text={buttonLabel} onClick={() => submitReply()} />
            </StyledToolbar>} onChange={() => debouncedValidate()} onFocus={() => {
      debouncedValidate();
      if (showGuidelines(currentCommunity)) {
        dispatch(showModal({
          modalType: ModalType.CommunityGuidelines,
          modalProps: {
            messageEditor: editor
          }
        }));
      }
    }} />}
    </ReplyEditorWrapper>;
}
const ReplyEditorWrapper = styled(EditorContainer)`
  padding-bottom: 0;
  margin-top: 15px;
`;
const StyledToolbar = styled(EditorToolbar)`
  margin-top: 0;
  margin-bottom: 0;
  padding: 5px 0;
  line-height: 1.8;
`;
const ReplyButton = styled(Button)`
  float: right;
  margin: 0;
  @media (${({
  theme
}) => theme.underScreenXsmall}) {
   margin-top: 15px;
  }
`;